<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import { socketEmitAsync } from '@/utils/web-socket'
import type { CaveGetInfo } from '@/types/one-year'
import { ref } from 'vue'
import { copyText } from '@/utils/copy-text'
import { useCommonStore } from '@/stores/modules/common'
import { watch } from 'vue'
import { onMounted } from 'vue'

const commonStore = useCommonStore()
const userStore = useUserStore()

const curTab = ref('cave')

const caveGetInfo = ref<CaveGetInfo>()
async function getCaveGetInfo() {
  const res = await socketEmitAsync<CaveGetInfo>({ type: 'getSecretCaveFightGetInfo' })
  caveGetInfo.value = res
}
async function handleGenCaveCode() {
  if (!caveGetInfo.value) return
  const res = await socketEmitAsync<string>({ type: 'generateSecretCaveFightCode' })
  caveGetInfo.value.code = res
}
function handleGiftFight() {
  ElMessageBox.prompt('请输入对方的求赠编码', '赠送挑战券', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputPattern: /^[a-zA-Z0-9]{6}$/,
    inputErrorMessage: '错误的编码格式',
    confirmButtonClass: 'is-plain',
    async beforeClose(action, instance, done) {
      if (action !== 'confirm') return done()
      try {
        instance.confirmButtonLoading = true
        instance.confirmButtonText = '加载中...'
        const value = instance.inputValue
        await socketEmitAsync({ type: 'giftSecretCaveFight', data: { code: value } })
        done()
      } finally {
        instance.confirmButtonLoading = false
        instance.confirmButtonText = '确定'
      }
    },
  })
}
const giftUserList = ref<{ nickname: string; nicknameColor: string; giftCode: string }[]>()
function getGiftUserList() {
  if (!caveGetInfo.value || !caveGetInfo.value.code) return
  socketEmitAsync<{ nickname: string; nicknameColor: string; giftCode: string }[]>({
    type: 'viewSecretCaveFightGiftInfo',
  }).then((res) => {
    giftUserList.value = res
  })
}

onMounted(() => {
  getCaveGetInfo()
})
watch(
  () => commonStore.path,
  (val) => {
    if (val === 'OneYear') {
      getCaveGetInfo()
    }
  },
)
</script>

<template>
  <div class="border-wrap" style="margin-bottom: 10px">
    <h4>秘藏洞窟</h4>
    <p>持有挑战券：{{ userStore.consumables.secretCaveFight }}</p>
    <p class="affix" style="display: flex; align-items: center">
      <span style="margin-right: 15px"
        >共斗掉落：<span :class="{ grow: caveGetInfo?.getCount === 5 }">{{ caveGetInfo?.getCount || 0 }} / 5</span></span
      >
      <span
        >求赠获得：<span :class="{ grow: caveGetInfo?.getGiftCount === 3 }">{{ caveGetInfo?.getGiftCount || 0 }} / 3</span></span
      >
      <el-popover
        placement="bottom"
        :width="300"
        trigger="click"
        popper-class="monster-detail"
        :teleported="false"
        :disabled="!caveGetInfo?.getGiftCount"
      >
        <template #reference>
          <el-button type="primary" size="small" plain v-show="(caveGetInfo?.getGiftCount || 0) > 0" @click="getGiftUserList" style="margin-left: 5px"
            >查看赠礼</el-button
          >
        </template>
        <table border style="width: 100%; text-align: center">
          <tr>
            <th>昵称</th>
            <th>对方求赠码</th>
          </tr>
          <tr v-for="item of giftUserList">
            <td>
              <span class="nickname" :style="{ background: item.nicknameColor }">{{ item.nickname }}</span>
            </td>
            <td>
              <span
                >{{ item.giftCode }}
                <el-button type="primary" size="small" plain v-if="item.giftCode" @click="copyText(item.giftCode)" style="margin-left: 5px"
                  >复制</el-button
                ></span
              >
            </td>
          </tr>
        </table>
      </el-popover>
    </p>
    <p style="display: flex; align-items: center; user-select: text">
      求赠编码：{{ caveGetInfo?.code || '（共斗掉落达 5次后可生成及赠礼）' }}
      <el-button
        type="success"
        size="small"
        plain
        :disabled="caveGetInfo?.getCount !== 5"
        v-if="!caveGetInfo?.code"
        @click="handleGenCaveCode"
        style="margin-left: 5px"
        >生成</el-button
      >
      <el-button type="primary" size="small" plain v-if="caveGetInfo?.code" @click="copyText(caveGetInfo.code)" style="margin-left: 5px"
        >复制</el-button
      >
    </p>
    <el-button type="success" plain @click="commonStore.path = 'Dungeon'">前往挑战</el-button>
    <el-button type="primary" plain :disabled="caveGetInfo?.getCount !== 5" @click="handleGiftFight">赠送挑战券</el-button>
  </div>
  <div class="border-wrap affix">
    <h4>秘藏洞窟说明</h4>
    <p>1. 周年庆期间参与共斗即有概率获得<span class="info">🍄 秘藏洞窟挑战券</span>，用于在洞窟中获取丰厚奖励及周年积分。</p>
    <p>
      2. 每周最多可获得 5<span class="info">秘藏洞窟挑战券</span>，当获取 <span class="grow">5</span>份后，将获得<span class="info">求赠码</span>。
    </p>
    <p>
      3. 将<span class="info">求赠码</span>分享给其他玩家，当其他玩家填入您的求赠码，您将额外获得<span class="info">秘藏洞窟挑战券</span
      >，每周最多可求赠 <span class="grow">3</span>份。
    </p>
  </div>
</template>

<style lang="scss" scoped>
.activity {
  h4 {
    position: relative;
    .help-info {
      position: absolute;
      top: 2px;
      right: 10px;
    }
  }
  .border-wrap {
    padding: 10px;
    p {
      font-size: 14px;
      margin: 5px 0;
    }
  }
}
</style>
